import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ReactNativeBlobUtil from 'react-native-blob-util'
import {
  pick,
  types as documentTypes,
  isErrorWithCode,
  errorCodes,
  keepLocalCopy,
} from '@react-native-documents/picker'
import type { FileToCopy } from '@react-native-documents/picker'
import { launchImageLibrary } from 'react-native-image-picker'
import type { ImageLibraryOptions } from 'react-native-image-picker'
import { useActionBottomSheet } from '@src/components'
import { useDirectUpload } from '@src/hooks/useDirectUpload'
import { UserFileTag } from '@src/types'
import { UploadFileModal } from '@src/screens/Nora/components/UploadFileModal'
import { useSnack } from '@src/utils/navigatorContext'

const MAX_ATTACHMENTS = 4
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024

const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'heic', 'heif'])
const PDF_EXTENSIONS = new Set(['pdf'])
const IMAGE_MIME_TYPES = new Set(['image/png', 'image/jpeg', 'image/heic', 'image/heif'])
const PDF_MIME_TYPES = new Set(['application/pdf'])

const MIME_TYPE_BY_EXTENSION: Record<string, string> = {
  heic: 'image/heic',
  heif: 'image/heif',
  jpeg: 'image/jpeg',
  jpg: 'image/jpeg',
  pdf: 'application/pdf',
  png: 'image/png',
}

const buildImageLibraryOptions = (selectionLimit: number): ImageLibraryOptions => ({
  mediaType: 'photo',
  selectionLimit,
  assetRepresentationMode: 'current',
})

const buildAttachmentLocalId = (index: number) =>
  `${Date.now()}-${index}-${Math.random().toString(16).slice(2)}`

type AttachmentKind = 'image' | 'pdf'

export interface NoraAttachmentItem {
  localId: string
  fileName: string
  mimeType: string
  sizeInBytes: number
  uri: string
}

export interface NoraUploadedAttachmentPreview {
  downloadUrl: string
  fileName: string
  id: string
  mimeType: string
}

interface PrepareAttachmentInput {
  kind: AttachmentKind
  fileName: string
  index: number
  localUri: string
  pickerSize: string | number | null | undefined
  providedMimeType: string | null | undefined
  shouldCleanupOnFailure?: boolean
  shouldTreatAsRequestedType?: boolean
}

const filePathFromUri = (uri: string) => decodeURI(uri).replace(/^file:\/\//, '')

const deleteLocalCopy = async (uri: string) => {
  try {
    const path = filePathFromUri(uri)
    const exists = await ReactNativeBlobUtil.fs.exists(path)

    if (exists) {
      await ReactNativeBlobUtil.fs.unlink(path)
    }
  } catch {
    // Best effort cleanup for temporary local copies.
  }
}

const extensionFromFileName = (fileName: string | null | undefined) => {
  if (!fileName) {
    return ''
  }

  const extension = fileName.split('.').pop()
  return extension ? extension.toLowerCase() : ''
}

const toBytes = (value: string | number | null | undefined): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (typeof value === 'string') {
    const parsed = Number.parseInt(value, 10)
    return Number.isFinite(parsed) ? parsed : undefined
  }

  return undefined
}

const buildFallbackName = (kind: AttachmentKind) => {
  const extension = kind === 'pdf' ? 'pdf' : 'jpg'
  return `upload-${Date.now()}.${extension}`
}

const resolveMimeType = ({
  fileName,
  providedMimeType,
}: {
  fileName: string
  providedMimeType: string | null
}) => {
  if (providedMimeType) {
    return providedMimeType.toLowerCase().split(';')[0].trim()
  }

  return MIME_TYPE_BY_EXTENSION[extensionFromFileName(fileName)] || 'application/octet-stream'
}

const isValidType = ({
  kind,
  fileName,
  mimeType,
}: {
  kind: AttachmentKind
  fileName: string
  mimeType: string
}) => {
  const extension = extensionFromFileName(fileName)

  if (kind === 'image') {
    return IMAGE_MIME_TYPES.has(mimeType) || IMAGE_EXTENSIONS.has(extension)
  }

  return PDF_MIME_TYPES.has(mimeType) || PDF_EXTENSIONS.has(extension)
}

const errorMessageForKind = (kind: AttachmentKind) =>
  kind === 'image'
    ? 'Only PNG, JPEG, HEIC, and HEIF files are supported.'
    : 'Only PDF files are supported.'

const sizeErrorMessageForKind = (kind: AttachmentKind) =>
  kind === 'image' ? 'Image size must be less than 5 MB.' : 'PDF size must be less than 5 MB.'

export const useNoraAttachmentUpload = () => {
  const showSnack = useSnack()
  const uploadFile = useDirectUpload()
  const showActionBottomSheet = useActionBottomSheet()
  const [attachments, setAttachments] = useState<NoraAttachmentItem[]>([])
  const [uploadedFileIds, setUploadedFileIds] = useState<string[]>([])
  const [uploadedAttachmentPreviews, setUploadedAttachmentPreviews] = useState<
    NoraUploadedAttachmentPreview[]
  >([])
  const [isUploading, setIsUploading] = useState(false)
  const attachmentsRef = useRef<NoraAttachmentItem[]>([])
  const uploadedFileIdsRef = useRef<string[]>([])
  const uploadedAttachmentPreviewsRef = useRef<NoraUploadedAttachmentPreview[]>([])

  useEffect(() => {
    attachmentsRef.current = attachments
  }, [attachments])

  useEffect(
    () => () => {
      void Promise.all(attachmentsRef.current.map((attachment) => deleteLocalCopy(attachment.uri)))
    },
    [],
  )

  const setUploadedAttachmentData = useCallback(
    ({ ids, previews }: { ids: string[]; previews: NoraUploadedAttachmentPreview[] }) => {
      uploadedFileIdsRef.current = ids
      uploadedAttachmentPreviewsRef.current = previews
      setUploadedFileIds(ids)
      setUploadedAttachmentPreviews(previews)
    },
    [],
  )

  const removeAttachment = useCallback(
    (localId: string) => {
      setUploadedAttachmentData({ ids: [], previews: [] })
      setAttachments((previous) => {
        const nextAttachments = previous.filter((attachment) => attachment.localId !== localId)
        const removedAttachments = previous.filter((attachment) => attachment.localId === localId)

        void Promise.all(removedAttachments.map((attachment) => deleteLocalCopy(attachment.uri)))

        return nextAttachments
      })
    },
    [setUploadedAttachmentData],
  )

  const clearAttachments = useCallback(() => {
    setUploadedAttachmentData({ ids: [], previews: [] })
    setAttachments((previous) => {
      void Promise.all(previous.map((attachment) => deleteLocalCopy(attachment.uri)))
      return []
    })
  }, [setUploadedAttachmentData])

  const resolveFileSize = useCallback(
    async ({
      localUri,
      pickerSize,
    }: {
      localUri: string
      pickerSize: string | number | null | undefined
    }): Promise<number | undefined> => {
      const normalizedPickerSize = toBytes(pickerSize)
      if (normalizedPickerSize) {
        return normalizedPickerSize
      }

      try {
        const stat = await ReactNativeBlobUtil.fs.stat(filePathFromUri(localUri))
        return toBytes(stat.size)
      } catch {
        return undefined
      }
    },
    [],
  )

  const prepareAttachmentItem = useCallback(
    async ({
      kind,
      fileName,
      index,
      localUri,
      pickerSize,
      providedMimeType,
      shouldCleanupOnFailure = false,
      shouldTreatAsRequestedType = true,
    }: PrepareAttachmentInput): Promise<NoraAttachmentItem | null> => {
      const cleanupIfNeeded = () => {
        if (shouldCleanupOnFailure) {
          void deleteLocalCopy(localUri)
        }
      }

      const mimeType = resolveMimeType({
        fileName,
        providedMimeType: providedMimeType || null,
      })

      if (!shouldTreatAsRequestedType || !isValidType({ kind, fileName, mimeType })) {
        cleanupIfNeeded()
        showSnack('Unsupported file type', errorMessageForKind(kind), 'error')
        return null
      }

      const sizeInBytes = await resolveFileSize({
        localUri,
        pickerSize,
      })

      if (!sizeInBytes) {
        cleanupIfNeeded()
        showSnack('File upload failed', 'Could not determine file size. Please try again.', 'error')
        return null
      }

      if (sizeInBytes > MAX_FILE_SIZE_BYTES) {
        cleanupIfNeeded()
        showSnack('File size limit exceeded', sizeErrorMessageForKind(kind), 'error')
        return null
      }

      return {
        localId: buildAttachmentLocalId(index),
        fileName,
        mimeType,
        sizeInBytes,
        uri: localUri,
      }
    },
    [resolveFileSize, showSnack],
  )

  const pickImagesFromLibrary = useCallback(
    async ({ remainingSlots }: { remainingSlots: number }) => {
      try {
        const response = await launchImageLibrary(buildImageLibraryOptions(remainingSlots))

        if (response.didCancel) {
          return []
        }

        if (response.errorCode === 'permission') {
          showSnack(
            'Photo access needed',
            'Allow photo library access to upload an image.',
            'warning',
          )
          return []
        }

        if (response.errorCode || response.errorMessage) {
          showSnack('File upload failed', 'Please try again in a few moments.', 'error')
          return []
        }

        if (!response.assets?.length) {
          return []
        }

        const preparedFiles: NoraAttachmentItem[] = []

        for (const [index, selectedAsset] of response.assets.entries()) {
          const localUri = selectedAsset.uri ? decodeURI(selectedAsset.uri) : null

          if (!localUri) {
            showSnack(
              'Upload failed',
              'We were unable to prepare one of the selected images. Please try again.',
              'error',
            )
            continue
          }

          const fileName = selectedAsset.fileName || buildFallbackName('image')
          const attachment = await prepareAttachmentItem({
            kind: 'image',
            fileName,
            index,
            localUri,
            pickerSize: selectedAsset.fileSize,
            providedMimeType: selectedAsset.type,
          })

          if (!attachment) {
            continue
          }

          preparedFiles.push(attachment)
        }

        return preparedFiles
      } catch {
        showSnack('File upload failed', 'Please try again in a few moments.', 'error')
        return []
      }
    },
    [prepareAttachmentItem, showSnack],
  )

  const pickPdfFilesFromDocumentPicker = useCallback(
    async ({ remainingSlots }: { remainingSlots: number }) => {
      try {
        const selectedFiles = await pick({
          type: [documentTypes.pdf],
          allowMultiSelection: true,
        })

        if (!selectedFiles.length) {
          return []
        }

        const selectedFilesWithinLimit =
          selectedFiles.length > remainingSlots
            ? selectedFiles.slice(0, remainingSlots)
            : selectedFiles

        if (selectedFiles.length > remainingSlots) {
          showSnack('File limit reached', `You can upload up to ${MAX_ATTACHMENTS} files.`, 'error')
        }

        const filesToCopy: FileToCopy[] = selectedFilesWithinLimit.map((file, index) => ({
          uri: file.uri,
          fileName: file.name || buildFallbackName('pdf') || `upload-${index}`,
        }))

        if (!filesToCopy.length) {
          return []
        }

        const [firstFileToCopy, ...remainingFilesToCopy] = filesToCopy
        const copyResults = await keepLocalCopy({
          files: [firstFileToCopy, ...remainingFilesToCopy],
          destination: 'documentDirectory',
        })

        const preparedFiles: NoraAttachmentItem[] = []

        for (const [index, selectedFile] of selectedFilesWithinLimit.entries()) {
          const copyResult = copyResults[index]

          if (copyResult.status !== 'success') {
            showSnack(
              'Upload failed',
              'We were unable to prepare one of the selected files. Please try again.',
              'error',
            )
            continue
          }

          const attachment = await prepareAttachmentItem({
            kind: 'pdf',
            fileName: selectedFile.name || buildFallbackName('pdf'),
            index,
            localUri: decodeURI(copyResult.localUri),
            pickerSize: selectedFile.size,
            providedMimeType: selectedFile.type,
            shouldCleanupOnFailure: true,
            shouldTreatAsRequestedType: selectedFile.hasRequestedType,
          })

          if (!attachment) {
            continue
          }

          preparedFiles.push(attachment)
        }

        return preparedFiles
      } catch (error) {
        if (isErrorWithCode(error) && error.code === errorCodes.OPERATION_CANCELED) {
          return []
        }
        showSnack('File upload failed', 'Please try again in a few moments.', 'error')
        return []
      }
    },
    [prepareAttachmentItem, showSnack],
  )

  const pickFilesForKind = useCallback(
    ({ kind, remainingSlots }: { kind: AttachmentKind; remainingSlots: number }) => {
      if (kind === 'image') {
        return pickImagesFromLibrary({ remainingSlots })
      }

      return pickPdfFilesFromDocumentPicker({ remainingSlots })
    },
    [pickImagesFromLibrary, pickPdfFilesFromDocumentPicker],
  )

  const pickAndStoreByKind = useCallback(
    async (kind: AttachmentKind) => {
      const remainingSlots = MAX_ATTACHMENTS - attachments.length

      if (remainingSlots <= 0) {
        showSnack('File limit reached', `You can upload up to ${MAX_ATTACHMENTS} files.`, 'error')
        return
      }

      const preparedFiles = await pickFilesForKind({ kind, remainingSlots })
      if (!preparedFiles.length) {
        return
      }
      setUploadedAttachmentData({ ids: [], previews: [] })
      setAttachments((previous) => [...previous, ...preparedFiles])
    },
    [attachments.length, pickFilesForKind, setUploadedAttachmentData, showSnack],
  )

  const openAttachmentPicker = useCallback(() => {
    showActionBottomSheet({
      body: (
        <UploadFileModal
          onImagePress={() => {
            void pickAndStoreByKind('image')
          }}
          onPdfPress={() => {
            void pickAndStoreByKind('pdf')
          }}
        />
      ),
    })
  }, [pickAndStoreByKind, showActionBottomSheet])

  const uploadAttachmentsAndGetUserFileIds = useCallback(async () => {
    if (!attachments.length) {
      setUploadedAttachmentData({ ids: [], previews: [] })
      return []
    }

    if (uploadedFileIdsRef.current.length > 0) {
      return uploadedFileIdsRef.current
    }

    setIsUploading(true)

    try {
      const uploadedAttachments = await Promise.all(
        attachments.map(async (attachment) => {
          const uploadedFile = await uploadFile({
            uri: attachment.uri,
            fileTag: UserFileTag.ChatbotAttachment,
            filename: attachment.fileName,
            contentType: attachment.mimeType,
          })

          if (!uploadedFile?.id) {
            throw new Error(`Could not upload ${attachment.fileName}`)
          }

          return {
            id: uploadedFile.id,
            fileName: uploadedFile.fileName || attachment.fileName,
            mimeType: attachment.mimeType,
            downloadUrl: uploadedFile.downloadFileUrl || uploadedFile.permanentUrl,
          }
        }),
      )

      const uploadedIds = uploadedAttachments.map((attachment) => attachment.id)
      const previews = uploadedAttachments.map((attachment) => ({
        id: attachment.id,
        fileName: attachment.fileName,
        mimeType: attachment.mimeType,
        downloadUrl: attachment.downloadUrl,
      }))

      setUploadedAttachmentData({ ids: uploadedIds, previews })
      return uploadedIds
    } catch (error: any) {
      showSnack(
        'File upload failed',
        error?.message || 'Could not upload selected files. Please try again.',
        'error',
      )
      throw error
    } finally {
      setIsUploading(false)
    }
  }, [attachments, setUploadedAttachmentData, showSnack, uploadFile])

  const getUploadedFileIds = useCallback(() => uploadedFileIdsRef.current, [])

  const getUploadedAttachmentPreviews = useCallback(() => uploadedAttachmentPreviewsRef.current, [])

  const canAttachMore = useMemo(() => attachments.length < MAX_ATTACHMENTS, [attachments.length])

  return {
    attachments,
    canAttachMore,
    clearAttachments,
    isUploading,
    openAttachmentPicker,
    removeAttachment,
    getUploadedAttachmentPreviews,
    getUploadedFileIds,
    uploadedAttachmentPreviews,
    uploadedFileIds,
    uploadAttachmentsAndGetUserFileIds,
  }
}
